import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';

import { User } from '@core/account-types';
import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import { EvaluationInsight } from '@core/evaluation-insights-types';
import { I18nService } from '@core/i18n-legacy';
import { HiddenResultPreview, InterviewResult } from '@core/interview-results-types';
import { Placement } from '@core/placements-types';
import { RtTooltip } from '@core/user-interface-legacy';
import { uniqByKey } from '@core/utils-legacy';

import { EvaluationMeterComponent } from './evaluation-meter.component';
import {
  getInterviewResultsForPlacement,
  getPlacementRatingStats,
  RatingStats,
} from './rating-stats';
import { EvaluationTooltipComponent } from '../evaluation-tooltip/evaluation-tooltip.component';

@Component({
  selector: 'rt-placement-evaluation-meter',
  template: ` <ng-template #evaluationMeterTooltip>
      <rt-evaluation-tooltip
        [tooltipText]="tooltipText()"
        [insight]="insight()"
        [isInsightsVisible]="isInsightsVisible()"
        [users]="users()"
      />
    </ng-template>
    <rt-evaluation-meter
      rtTooltip
      rtTooltipType="template-only"
      rtTooltipPosition="left"
      [rtTooltipHasNoArrow]="true"
      [rtTooltipTemplate]="evaluationMeterTooltip"
      [rtTooltipMaxWidth]="500"
      [rating]="rating()"
      [isDisqualified]="isCandidateDisqualified()"
      [isHidden]="isRatingHidden()"
      [evaluationScale]="evaluationScale()"
      [hasEvaluationImprovements]="hasEvaluationImprovements()"
    />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [EvaluationMeterComponent, EvaluationTooltipComponent, RtTooltip],
  host: {
    '(click)': 'dontCloseTooltipOnClick($event)',
  },
})
export class PlacementEvaluationMeterComponent {
  private readonly i18n = inject(I18nService);

  public placement = input.required<Placement>();

  public interviewResults = input.required<Record<number, InterviewResult[]>>();

  public hiddenResultPreview = input.required<HiddenResultPreview | undefined>();

  public evaluationInsights = input.required<EvaluationInsight[]>();

  public evaluationScale = input<RatingScale | null>(null);

  public hasEvaluationImprovements = input<boolean>(false);

  protected tooltipText = computed(() => {
    if (this.isRatingHidden() && this.hiddenResultPreview()) {
      return this.i18n.translate('app.candidate.placements.eval_meter.hidden_evals_tooltip', {
        count: this.hiddenResultPreview()!.count,
      });
    }

    if (this.rating() === null) {
      return this.i18n.translate('app.candidate.placements.eval_meter.no_evals_tooltip');
    }

    const insightSourcesCount = this.insight()?.sources.length ?? 0;
    if (insightSourcesCount > 0) {
      return this.i18n.translate('app.candidate.placements.eval_meter.key_takeaways_tooltip', {
        count: insightSourcesCount,
      });
    }

    const { evalCount } = this.ratingStats();
    return this.i18n.translate('app.candidate.placements.eval_meter.based_on_evals_tooltip', {
      count: evalCount,
    });
  });

  protected rating = computed(() => this.placement().positiveRatings ?? null);

  protected isRatingHidden = computed(
    () => this.placement().ratingVisible !== undefined && !this.placement().ratingVisible,
  );

  protected isCandidateDisqualified = computed(() => !!this.placement().disqualifyReason?.length);
  protected insight = computed(() => {
    const { offerId, talentPoolId } = this.placement();
    return (
      this.evaluationInsights().find(insight =>
        offerId ? offerId === insight.offer?.id : talentPoolId === insight.talentPool?.id,
      ) ?? null
    );
  });

  protected isInsightsVisible = computed(() => {
    const insight = this.insight();
    return !this.isRatingHidden() && !!insight && !insight.hidden;
  });
  protected users = computed(() => {
    const insight = this.insight();
    const results = insight
      ? insight.sources.flatMap(source => source.evaluation?.admin || source.evaluation?.guest)
      : getInterviewResultsForPlacement(this.placement(), this.interviewResults())
          .filter(([v]) => !!v)
          .flatMap(([_, interviewResult]) => interviewResult.map(r => r.admin || r.guest));
    return <User[]>uniqByKey(
      results.filter(v => !!v),
      'id',
    );
  });
  private ratingStats: Signal<RatingStats> = computed(() =>
    getPlacementRatingStats(this.placement(), this.interviewResults()),
  );

  protected dontCloseTooltipOnClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
  }
}
